import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const NotificationPreferences = ({ preferences, onUpdatePreferences }) => {
  const [formData, setFormData] = useState(preferences);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handlePreferenceChange = (category, type, value) => {
    const newFormData = {
      ...formData,
      [category]: {
        ...formData?.[category],
        [type]: value
      }
    };
    setFormData(newFormData);
    setHasChanges(JSON.stringify(newFormData) !== JSON.stringify(preferences));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await onUpdatePreferences(formData);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to update preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData(preferences);
    setHasChanges(false);
  };

  const notificationCategories = [
    {
      id: 'events',
      title: 'Event Notifications',
      description: 'Updates about events you\'re interested in',
      icon: 'Calendar',
      options: [
        { key: 'newEvents', label: 'New events in your area' },
        { key: 'eventUpdates', label: 'Updates to events you\'re attending' },
        { key: 'eventReminders', label: 'Event reminders (24h before)' }
      ]
    },
    {
      id: 'orders',
      title: 'Order & Ticket Updates',
      description: 'Important information about your purchases',
      icon: 'ShoppingCart',
      options: [
        { key: 'orderConfirmation', label: 'Order confirmations' },
        { key: 'ticketDelivery', label: 'Ticket delivery notifications' },
        { key: 'refundUpdates', label: 'Refund and cancellation updates' }
      ]
    },
    {
      id: 'marketing',
      title: 'Marketing & Promotions',
      description: 'Special offers and promotional content',
      icon: 'Tag',
      options: [
        { key: 'promotions', label: 'Special offers and discounts' },
        { key: 'newsletter', label: 'Weekly newsletter' },
        { key: 'recommendations', label: 'Personalized event recommendations' }
      ]
    },
    {
      id: 'account',
      title: 'Account & Security',
      description: 'Important account and security notifications',
      icon: 'Shield',
      options: [
        { key: 'securityAlerts', label: 'Security alerts and login notifications' },
        { key: 'accountUpdates', label: 'Account changes and updates' },
        { key: 'pointsUpdates', label: 'Points and rewards updates' }
      ]
    }
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
          <Icon name="Bell" size={20} color="white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Notification Preferences</h2>
          <p className="text-sm text-muted-foreground">Control how we communicate with you</p>
        </div>
      </div>
      <div className="space-y-6">
        {notificationCategories?.map((category) => (
          <div key={category?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                <Icon name={category?.icon} size={16} className="text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">{category?.title}</h3>
                <p className="text-sm text-muted-foreground">{category?.description}</p>
              </div>
            </div>

            <div className="space-y-4">
              {category?.options?.map((option) => (
                <div key={option?.key} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{option?.label}</span>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <Checkbox
                      label="Email"
                      checked={formData?.[category?.id]?.[option?.key]?.email || false}
                      onChange={(e) => handlePreferenceChange(category?.id, option?.key, {
                        ...formData?.[category?.id]?.[option?.key],
                        email: e?.target?.checked
                      })}
                      size="sm"
                    />
                    
                    <Checkbox
                      label="SMS"
                      checked={formData?.[category?.id]?.[option?.key]?.sms || false}
                      onChange={(e) => handlePreferenceChange(category?.id, option?.key, {
                        ...formData?.[category?.id]?.[option?.key],
                        sms: e?.target?.checked
                      })}
                      size="sm"
                    />
                    
                    <Checkbox
                      label="Push"
                      checked={formData?.[category?.id]?.[option?.key]?.push || false}
                      onChange={(e) => handlePreferenceChange(category?.id, option?.key, {
                        ...formData?.[category?.id]?.[option?.key],
                        push: e?.target?.checked
                      })}
                      size="sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {hasChanges && (
        <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-border">
          <Button
            variant="default"
            onClick={handleSave}
            loading={isLoading}
            iconName="Save"
            className="flex-1"
          >
            Save Preferences
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={isLoading}
            className="flex-1"
          >
            Reset Changes
          </Button>
        </div>
      )}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-1">Important Note:</p>
            <p>
              Some notifications like order confirmations and security alerts cannot be disabled 
              as they are essential for your account security and order management.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;